export const PAGE_SIZE = 10

/**
 * Slice a list for 0-based client pagination.
 * @param {Array} items
 * @param {number} page
 * @param {number} [pageSize=PAGE_SIZE]
 * @returns {{ pageItems: Array, page: number, pageCount: number, total: number }}
 */
export function paginateItems(items, page, pageSize = PAGE_SIZE) {
    const list = Array.isArray(items) ? items : []
    const size = pageSize > 0 ? pageSize : PAGE_SIZE
    const total = list.length
    const pageCount = total === 0 ? 0 : Math.ceil(total / size)
    let safePage = Number(page) || 0
    if (safePage < 0) safePage = 0
    if (pageCount === 0) safePage = 0
    else if (safePage > pageCount - 1) safePage = pageCount - 1
    const start = safePage * size
    return {
        pageItems: list.slice(start, start + size),
        page: safePage,
        pageCount,
        total
    }
}

/**
 * Build a selection map that turns every id on or off.
 * @param {Array<string|number>} ids
 * @param {boolean} selected
 * @returns {Object<string, boolean>}
 */
export function selectionMapForAll(ids, selected) {
    const map = {}
    for (const id of ids || []) {
        map[id] = !!selected
    }
    return map
}

/**
 * @param {Array<string|number>} ids
 * @param {Object} selectionMap
 * @param {(id: string|number, map: Object) => boolean} isOn
 * @returns {boolean}
 */
export function isFullySelected(ids, selectionMap, isOn) {
    const list = ids || []
    if (!list.length) return true
    const map = selectionMap || {}
    return list.every((id) => isOn(id, map))
}

/**
 * Campaign ids whose audience is selected (or that have no list).
 * @param {Array<{ id: string|number, list_id?: string }>} campaigns
 * @param {(listId: string) => boolean} listOn
 * @returns {Array<string|number>}
 */
export function eligibleCampaignIds(campaigns, listOn) {
    return (campaigns || [])
        .filter((campaign) => !campaign.list_id || listOn(campaign.list_id))
        .map((campaign) => campaign.id)
}

/**
 * Tags for fields that can be toggled off in preview (non-mandatory).
 * @param {Array<{ tag: string, mandatory?: boolean }>} fields
 * @returns {Array<string>}
 */
export function optionalFieldTags(fields) {
    return (fields || []).filter((field) => field && !field.mandatory && field.tag).map((field) => field.tag)
}

/**
 * Thumbnail treatment for an import-preview template.
 * Screenshot templates and campaign-recovered templates (no Mailchimp thumbnail)
 * both use the expandable preview control. Only templates with neither stay a static wireframe.
 * @param {{ thumbnail?: string|null, sourceCampaignId?: string|null }} template
 * @returns {'image'|'live'|'plain'}
 */
export function previewThumbModifier(template) {
    if (template && template.thumbnail) return 'image'
    if (template && template.sourceCampaignId) return 'live'
    return 'plain'
}
